
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { Notification } from '@/entities/notification';
import { notificationsApi } from '@/shared/api';
import { queryClient } from '@/shared/lib/query-client';

export const POPUP_LIMIT = 20;

export const notificationKeys = {
  all: ['notifications'] as const,
  popup: () => [...notificationKeys.all, 'popup', POPUP_LIMIT] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

export const popupQuery = queryOptions({
  queryKey: notificationKeys.popup(),
  queryFn: async (): Promise<Notification[]> => {
    const res = await notificationsApi.list({ limit: POPUP_LIMIT, unreadOnly: true });
    const { data, total } = res as unknown as { data?: Notification[]; total?: number };
    if (typeof total === 'number') queryClient.setQueryData<number>(notificationKeys.unreadCount(), total);
    return Array.isArray(data) ? data : [];
  },
});

export const unreadCountQuery = queryOptions({
  queryKey: notificationKeys.unreadCount(),
  queryFn: async (): Promise<number> => {
    const res = await notificationsApi.getUnreadCount();
    return (res as unknown as { data?: { count?: number } })?.data?.count ?? 0;
  },
});

export function useNotificationsPopup(enabled = true) {
  return useQuery({ ...popupQuery, enabled });
}

export function useUnreadCount(enabled = true) {
  return useQuery({ ...unreadCountQuery, enabled });
}

export function useHasUnreadForTicket(ticketId: string | undefined) {
  const { data } = useQuery({
    ...popupQuery,
    enabled: false,
    select: (items) => items.some((n) => n.ticketId === ticketId && !n.readAt),
  });
  return !!ticketId && !!data;
}


function setPopup(
  qc: QueryClient,
  updater: (items: Notification[]) => Notification[],
) {
  qc.setQueryData<Notification[]>(notificationKeys.popup(), (prev) => updater(prev ?? []));
}

function setUnread(qc: QueryClient, updater: (count: number) => number) {
  qc.setQueryData<number>(notificationKeys.unreadCount(), (prev) => Math.max(0, updater(prev ?? 0)));
}

export const notificationsCache = {
  prepend(n: Notification, qc: QueryClient = queryClient) {
    setPopup(qc, (items) => [n, ...items.filter((i) => i.id !== n.id)].slice(0, POPUP_LIMIT));
    if (!n.readAt) setUnread(qc, (c) => c + 1);
    void qc.invalidateQueries({ queryKey: notificationKeys.popup(), refetchType: 'none' });
  },

  removeOne(id: string, qc: QueryClient = queryClient) {
    setPopup(qc, (items) => items.filter((n) => n.id !== id));
    setUnread(qc, (c) => c - 1);
  },

  removeMany(ids: string[], qc: QueryClient = queryClient) {
    const gone = new Set(ids);
    setPopup(qc, (items) => items.filter((n) => !gone.has(n.id)));
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  },

  removeByTicket(ticketId: string, qc: QueryClient = queryClient) {
    setPopup(qc, (items) => items.filter((n) => n.ticketId !== ticketId));
  },

  clear(qc: QueryClient = queryClient) {
    setPopup(qc, () => []);
    qc.setQueryData<number>(notificationKeys.unreadCount(), 0);
  },

  refetchUnreadCount(qc: QueryClient = queryClient) {
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  },

  resync(qc: QueryClient = queryClient) {
    void qc.invalidateQueries({ queryKey: notificationKeys.popup() });
    void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  },

  reset(qc: QueryClient = queryClient) {
    qc.removeQueries({ queryKey: notificationKeys.all });
  },

  prefetchPopup(qc: QueryClient = queryClient) {
    void qc.prefetchQuery(popupQuery);
  },
};
